/* Copyright (c) 2021 MOCA Systems, Inc.  All rights reserved. */

import { Observable, BehaviorSubject, Subscription } from 'rxjs';

import * as moment from 'moment';
import * as bowser from 'bowser';

import { sessionErrorTracking } from '../services/SessionErrorTracking';
import { environment } from '../../../environments/environment-variables';

import { throttleError } from 'utils/basic-utils';
import { analyticsService } from 'src/app/plain-javascript/services/analytics/AnalyticsService';
import { ENV_CONFIG } from 'src/app/app-data/constants/dynamic-env';
import { fancyFirebaseGetters, fancyFirebaseReferencer } from '@touchplan/fancy-referencer';
import { logger } from '@touchplan/logging';

import firebase from 'firebase/compat/app';

const additionalKey = environment.additionalErrorIdentifier;
const gitCommit = ENV_CONFIG.shortSha;

//essentially "by design" fbReporting will not necessarily have access to the userId, especially really early.
// it will operate on a best try method, so it will have it as soon as it reasonably can.
export class FbReporting {
	private userId$ = new BehaviorSubject<string>(null);
	private subscribers: Subscription[] = [];

	private errThrottle = throttleError((errorObject: any, extra?) => {
		this.reportEvent('errors', errorObject, extra);
		this.reportToAnalytics(errorObject, extra, 'error-client');
	}, -10000);

	private reportEvent(eventType: string, eventNameOrErrorObject: string | any, extra) {
		if (!eventNameOrErrorObject) {
			return;
		}
		if (!eventType) {
			eventType = 'events';
		}

		const time = moment().format('YYYY-MM-DD');
		// var refUrl = 'events/'+validateRefUrl(eventType)+'/'+validateRefUrl(time)+'/'+validateRefUrl(eventName);
		const eventsPath = fancyFirebaseGetters.getEventsPath();
		const ref = fancyFirebaseReferencer
			.getLegacyRef<firebase.database.Reference>(eventsPath)
			.child(validateRefUrl(eventType))
			.child(validateRefUrl(time))
			.child(validateRefUrl(eventNameOrErrorObject));

		const ua = bowser.getParser(window.navigator.userAgent);
		const browserName = ua.getBrowserName() + ' ' + ua.getBrowserVersion();

		const eventObj: any = {
			timestamp: moment().format(),
			message: eventNameOrErrorObject,
			browser: browserName,
			agent: ua.getResult(),
		};
		if (additionalKey) {
			eventObj[additionalKey] = true;
		}
		if (gitCommit) {
			eventObj.build = gitCommit;
		}

		if (extra) {
			extra.prevErrorCount = sessionErrorTracking.count;
			extra.firstError = sessionErrorTracking.firstError();
			extra.recentErrors = sessionErrorTracking.lastErrors();

			eventObj['extra'] = extra;
		}
		eventObj['userId'] = this.userId$.getValue();
		eventObj['sessionLengthInSeconds'] = sessionErrorTracking.sessionLengthS;
		eventObj.session = sessionInfo();

		//could fix this and make it transaction-y, but...why?
		const countRef = ref.child('count');
		countRef.once('value', function (dataSnapshot) {
			if (!dataSnapshot.exists()) {
				countRef.set(1);
			} else {
				countRef.set(dataSnapshot.val() + 1);
			}

			const childRef = ref.child('instances');
			logger.log('fbReporting', childRef.toString());
			childRef.push(eventObj);
		});
	}

	public reportError(error: any, extra?) {
		if (window.errorDebugging) {
			debugger;
		}
		this.errThrottle(error, extra);
	}
	public reportWarning(message, extra?) {
		this.reportEvent('warnings', message, extra);
		this.reportToAnalytics(message, extra);
	}

	//since people have decided to make normal warnings worthless...
	public reportRealWarning(message, extra?) {
		this.reportEvent('errors', message, extra);
		this.reportToAnalytics(message, extra, 'error-client');
	}

	public reportNotice(message, extra?) {
		this.reportEvent('notice', message, extra);
	}

	public reportToAnalytics(msg, extra?, customEvent?) {
		const obj = {
			message: msg,
		};
		if (extra) {
			Object.assign(obj, extra);
		}
		if (!customEvent) {
			customEvent = 'warning-client';
		}
		if (analyticsService) {
			analyticsService.track(customEvent, obj);
		} else {
			logger.log('fbReporting', 'not reporting error due to analytics not existing');
		}
	}

	public addUserIdOb$(obs: Observable<string>) {
		this.subscribers.push(obs.subscribe(this.userId$));
	}
	//this can be used to force it before another call
	public setUserId(userId: string) {
		this.userId$.next(userId);
	}

	//this should be a long running session wide thing, so there's no particular reason this needs to
	//clean up, but just in case.
	public destroy() {
		this.subscribers.forEach((s) => s.unsubscribe());
	}
}
export const fbReporting = new FbReporting();

function validateRefUrl(urlOrEventObject: string | any) {
	let decoded = urlOrEventObject;
	if (typeof urlOrEventObject !== 'string') {
		decoded = (urlOrEventObject as Error).message;
	}
	if (decoded.includes('%')) {
		// TP-2082: "decodeURIComponent" throws an exception if it's given a string with a % sign, so...
		decoded = decoded.replace(/\%/g, encodeURIComponent('%'));
	}
	decoded = decodeURIComponent(decoded);
	decoded = decoded.replace(/\//g, '|');
	decoded = decoded.replace(/\s/g, ' ');
	decoded = decoded.replace(/[.#$\[\]]/g, '*');
	return decoded;
}

function sessionInfo() {
	const obj: any = {
		screenWidth: window.outerHeight ? window.outerHeight : null,
		screenHeight: window.outerWidth ? window.outerWidth : null,
	};
	if (window.location && window.location.href) {
		obj.url = window.location.href;
	}

	return obj;
}
